import React, { useState, useEffect } from 'react';

import { OverlayPanel } from './OverlayPanel';

interface BottleContentModalProps {
  isOpen: boolean;
  type: string;
  content: string;
  reward?: {
    pieces?: number;
    itemId?: string;
    itemName?: string;
    treasureX?: number;
    treasureY?: number;
  };
  onDismiss: () => void;
}

export const BottleContentModal: React.FC<BottleContentModalProps> = ({
  isOpen,
  type,
  content,
  reward,
  onDismiss,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    }
  }, [isOpen]);

  // Don't return null - let OverlayPanel handle visibility
  if (!isOpen && !isVisible) return null;

  const titles: Record<string, string> = {
    lore: '[ANCIENT TALE]',
    hint: '[MYSTERIOUS HINT]',
    treasure: '[TREASURE MAP]',
    distress: '[DISTRESS SIGNAL]',
    joke: "[SAILOR'S NOTE]",
  };

  const title = titles[type] || '[MESSAGE]';
  const hasReward = reward && (reward.pieces || reward.itemName || reward.treasureX);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(onDismiss, 200);
  };

  return (
    <OverlayPanel
      isOpen={isOpen}
      onClose={handleDismiss}
      closeOnEscape
      closeOnBackdrop
      zIndex={6000}
      width={420}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          backgroundColor: '#0f172a',
          border: '2px solid #475569',
          transform: isVisible ? 'scale(1)' : 'scale(0.98)',
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 150ms ease, transform 150ms ease',
        }}
      >
        {/* Header Section */}
        <div style={{
          backgroundColor: '#1e293b',
          borderBottom: '1px solid #475569',
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h2 style={{
            color: '#94a3b8',
            fontSize: 11,
            fontWeight: 700,
            margin: 0,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            fontFamily: 'var(--font-mono)',
          }}>
            {title}
          </h2>
          <button
            type="button"
            onClick={handleDismiss}
            style={{
                background: 'transparent',
                border: '1px solid #21262d',
                color: '#9ca3af',
                padding: '2px 8px',
                cursor: 'pointer',
                fontFamily: 'var(--font-mono)',
              }}
          >
            [X]
          </button>
        </div>

        {/* Content Section */}
        <div style={{ padding: '16px' }}>
          <p
            className="font-lookout-21"
            style={{
              color: '#cbd5e1',
              textAlign: 'center',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              margin: '0 0 16px 0',
            }}
          >
            {content}
          </p>

          {hasReward && (
            <div style={{
              backgroundColor: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '4px',
              padding: '12px',
              marginBottom: '16px',
              textAlign: 'center',
            }}>
              <p style={{
                color: '#4ade80',
                fontSize: 11,
                margin: 0,
                fontWeight: 600,
                fontFamily: 'var(--font-mono)',
              }}>
                {reward?.pieces ? `+${reward.pieces} ₱` : ''}
                {reward?.pieces && reward?.itemName ? ' • ' : ''}
                {reward?.itemName ? `${reward.itemName}` : ''}
                {(reward?.pieces || reward?.itemName) && reward?.treasureX ? ' • ' : ''}
                {reward?.treasureX ? 'Treasure marked' : ''}
              </p>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={handleDismiss}
              style={{
                padding: '8px 24px',
                background: '#1e293b',
                border: '1px solid #475569',
                color: '#cbd5e1',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.5px',
                transition: 'all 150ms ease',
                fontFamily: 'var(--font-mono)',
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLButtonElement).style.backgroundColor = '#334155';
                (e.target as HTMLButtonElement).style.borderColor = '#64748b';
                (e.target as HTMLButtonElement).style.color = '#e2e8f0';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLButtonElement).style.backgroundColor = '#1e293b';
                (e.target as HTMLButtonElement).style.borderColor = '#475569';
                (e.target as HTMLButtonElement).style.color = '#cbd5e1';
              }}
            >
              DISMISS
            </button>
          </div>
        </div>
      </div>
    </OverlayPanel>
  );
};
